import { useCallback } from "react";
import { useImmer } from "use-immer";
import uuid from "uuid/v4";

import { gql, uploadFileAsResource } from "@examspark/api";
import { parseAwsJson } from "@examspark/common";
import { getSharedMediaTypeByMimeType } from "@examspark/common/dist/utils/file";
import { AllowableMimeType, FileUpload, Resource } from "@examspark/types";

import { uploadFile } from "../util";

export const useFileUpload = (
  params: Partial<{
    workspaceId: string;
    bucketName: string;
    region: string;
  }>,
) => {
  const [files, updateFiles] = useImmer<FileUpload[]>([]);

  const isLoading = files.some((f) =>
    ["IN_PROGRESS", "CHECKSUM_IN_PROGRESS"].includes(f.status ?? ""),
  );
  const hasFailures = files.some((f) =>
    ["FAILURE", "CHECKSUM_EXISTS"].includes(f.status ?? ""),
  );

  const updateFileById = useCallback(
    (fileId: string, data: Partial<FileUpload>) => {
      updateFiles((draft) => {
        const index = draft.findIndex((f) => f.id === fileId);
        if (index === -1) return;
        draft[index] = {
          ...draft[index],
          ...data,
        };
      });
    },
    [updateFiles],
  );

  const deleteFileById = useCallback(
    (fileId?: string) => {
      updateFiles((draft) => {
        const index = draft.findIndex((f) => f.id === fileId);
        if (index === -1) return;
        draft.splice(index, 1);
      });
    },
    [updateFiles],
  );

  const addFile = useCallback(
    async (
      file: File,
      data?: Partial<FileUpload>,
      options?: {
        getChecksum?: boolean;
      },
    ) => {
      const id = uuid();
      updateFiles((draft) => {
        draft.push({
          id,
          type: "hello",
          status: options?.getChecksum ? "CHECKSUM_IN_PROGRESS" : "PENDING",
          file,
          progress: {
            loaded: 0,
            total: file.size,
          },
          ...data,
        });
      });

      if (options?.getChecksum) {
        const result = await uploadFile(`uploads/${id}`, file, {
          isTemporary: true,
        });

        const checksum = result.properties?.eTag.replace(/"/g, "");

        const existing = await gql.operations.resource.search({
          variables: {
            limit: 0,
            filter: {
              checksum: {
                eq: checksum,
              },
              isDeleted: {
                ne: true,
              },
              workspaceId: {
                eq: params.workspaceId,
              },
            },
          },
        });

        const aggs = parseAwsJson(existing.data?.search.aggregations);
        const bankAggs = aggs?.bankId.buckets.map((b: any) => b.key);

        updateFileById(id, {
          checksum,
          status: "PENDING",
          checksumFoundInBankIds: bankAggs,
        });
      }
    },

    [params.workspaceId, updateFileById, updateFiles],
  );

  const clearFiles = useCallback(() => {
    updateFiles((draft) => {
      draft.splice(0, draft.length);
    });
  }, [updateFiles]);

  const deleteAtIndex = useCallback(
    (index: number) => {
      updateFiles((draft) => {
        draft.splice(index, 1);
      });
    },
    [updateFiles],
  );

  const uploadFiles = useCallback(async () => {
    if (!params.workspaceId) {
      console.error("No workspaceId provided");
      return;
    }

    // Warning: this is very specific to the bonfire/annotations UI. Need to
    // genericize this.
    return await Promise.all(
      files.map(async (file, index) => {
        if (!params.workspaceId) return;
        const upload = await uploadFileAsResource(
          file,
          params.workspaceId,
          async (key: string, file) => {
            return await uploadFile(key, file, {
              isTemporary: true,
              progressCallback: (progress: {
                loaded: number;
                total: number;
              }) => {
                updateFiles((draft) => {
                  draft[index].progress = progress;
                });
              },
            });
          },
        );

        const resource: Resource = {
          id: upload.id,
          sk: "resource#v0",
          schemaVersion: "2020-01-01",
          version: 0,
          type: getSharedMediaTypeByMimeType(
            file.file.type as AllowableMimeType,
          ),
          name: file.saveAs || file.file.name,
          workspaceId: params.workspaceId,
          // bankId: props.data?.bankId,
          // subject: props.data?.subject,
          // grade: props.data?.grade,
          // creatorId: session.user.id,
          maturity: "OPERATIONAL",
          file: {
            region: params.region,
            bucket: params.bucketName,
            key: `public/${upload.key}`,
            // name: fileUtils.getFilename(upload.key),
            size: file.file.size,
            mimetype: file.file.type,
            // extension: fileUtils.getExtension(upload.key),
          },
          checksum: upload?.properties?.eTag,
          isCreating: true,
        };

        await gql.operations.resource.createNumberedResource(resource);

        updateFiles((draft) => {
          draft[index].status = "SUCCESS";
        });

        return resource;
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, params.workspaceId, updateFiles]);

  return [
    files,
    {
      addFile,
      updateFileById,
      deleteAtIndex,
      deleteFileById,
      updateFiles,
      uploadFiles,
      clearFiles,
      isLoading,
      hasFailures,
    },
  ] as const;
};
